import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { Modal } from '../../components/common/Modal';
import { adminApi, AdminUser } from '../../api/admin.api';
import { AdminUserForm } from '../../components/users/AdminUserForm';
import { PermissionsForm } from '../../components/users/PermissionsForm';
import { ApiKeysPanel } from '../../components/users/ApiKeysPanel';
import { Plus, Edit, Trash2, Shield } from 'lucide-react';
import toast from 'react-hot-toast';

export const Users: React.FC = () => {
  const qc = useQueryClient();
  const [activeTab, setActiveTab] = React.useState<'admins' | 'api-keys'>('admins');

  const adminsQuery = useQuery({
    queryKey: ['admins', { page: 1, limit: 50 }],
    queryFn: () => adminApi.listAdmins(1, 50),
  });

 const statsQuery = useQuery({
    queryKey: ['admins-stats'],
    queryFn: () => adminApi.getAdminsStats(),
  });

  const [isCreateOpen, setCreateOpen] = React.useState(false);
  const [isEditOpen, setEditOpen] = React.useState<null | AdminUser>(null);
  const [permissionsTarget, setPermissionsTarget] = React.useState<null | AdminUser>(null);
  const [permissionsInitial, setPermissionsInitial] = React.useState<string[] | undefined>(undefined);

  const createMutation = useMutation({
    mutationFn: adminApi.createAdmin,
    onSuccess: () => {
      toast.success('Admin created');
      qc.invalidateQueries({ queryKey: ['admins'] });
      setCreateOpen(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<AdminUser> }) => adminApi.updateAdmin(id, data),
    onSuccess: () => {
      toast.success('Admin updated');
      qc.invalidateQueries({ queryKey: ['admins'] });
      setEditOpen(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => adminApi.deleteAdmin(id),
    onSuccess: () => {
      toast.success('Admin deleted');
      qc.invalidateQueries({ queryKey: ['admins'] });
    },
  });

  const permissionsMutation = useMutation({
    mutationFn: ({ id, permissions }: { id: string; permissions: string[] }) =>
      adminApi.updatePermissions(id, permissions),
    onSuccess: () => {
      toast.success('Permissions updated');
      qc.invalidateQueries({ queryKey: ['admins'] });
      setPermissionsTarget(null);
    },
  });

  const openPermissions = async (u: AdminUser) => {
    try {
      const res = await adminApi.getAdmin(u._id);
      setPermissionsInitial(res.data.permissions || []);
    } catch {
      setPermissionsInitial(u.permissions || []);
    }
    setPermissionsTarget(u);
  };

  const admins = adminsQuery.data?.data.items || [];
  const total = adminsQuery.data?.data.total || 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">User Admin</h1>
          <p className="text-gray-600">Manage admin users, permissions, and API keys</p>
        </div>
        {activeTab === 'admins' && (
          <Button icon={<Plus className="w-4 h-4" />} onClick={() => setCreateOpen(true)}>
            New Admin
          </Button>
        )}
      </div>

      <div className="flex gap-2 border-b">
        <button
          className={`py-2 px-3 text-sm ${activeTab === 'admins' ? 'border-b-2 border-primary-500 text-primary-600' : 'text-gray-600'}`}
          onClick={() => setActiveTab('admins')}
        >
          Admins
        </button>
        <button
          className={`py-2 px-3 text-sm ${activeTab === 'api-keys' ? 'border-b-2 border-primary-500 text-primary-600' : 'text-gray-600'}`}
          onClick={() => setActiveTab('api-keys')}
        >
          API Keys
        </button>
      </div>

      {activeTab === 'admins' ? (
        <div className="space-y-6">
          <div className="grid grid-cols-3 gap-4">
            <Card title="Admins">
              <div className="overflow-x-auto">
                {adminsQuery.isLoading ? (
                  <p className="text-gray-600">Loading...</p>
                ) : admins.length === 0 ? (
                  <p className="text-gray-600">No admins yet.</p>
                ) : (
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="text-left text-gray-600 border-b">
                        <th className="py-2 pr-4">Name</th>
                        <th className="py-2 pr-4">Username</th>
                        <th className="py-2 pr-4">Role</th>
                        <th className="py-2 pr-4">Active</th>
                        <th className="py-2 pr-4">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {admins.map((u: AdminUser) => (
                        <tr key={u._id} className="border-b last:border-0">
                          <td className="py-2 pr-4 font-medium">{u.name}</td>
                          <td className="py-2 pr-4">{u.username || u.email || '-'}</td>
                          <td className="py-2 pr-4">{u.role}</td>
                          <td className="py-2 pr-4">
                            <span
                              className={`px-2 py-0.5 rounded text-xs ${u.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-700'}`}
                            >
                              {u.isActive ? 'Yes' : 'No'}
                            </span>
                          </td>
                          <td className="py-2 pr-4">
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="ghost"
                                icon={<Shield className="w-4 h-4" />}
                                onClick={() => openPermissions(u)}
                              >
                                Permissions
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                icon={<Edit className="w-4 h-4" />}
                                onClick={() => setEditOpen(u)}
                              >
                                Edit
                              </Button>
                              <Button
                                size="sm"
                                variant="danger"
                                icon={<Trash2 className="w-4 h-4" />}
                                onClick={async () => {
                                  // eslint-disable-next-line no-restricted-globals
                                  if (!confirm(`Delete admin "${u.name}"?`)) return;
                                  await deleteMutation.mutateAsync(u._id);
                                }}
                                isLoading={deleteMutation.isPending}
                              >
                                Delete
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                {!adminsQuery.isLoading && total > 0 && (
                  <p className="text-xs text-gray-500 mt-3">{total} total</p>
                )}
              </div>
            </Card>

            <Card title="Stats" className="col-span-1">
              {statsQuery.isLoading ? (
                <p className="text-gray-600">Loading...</p>
              ) : (
                <pre className="text-xs bg-gray-50 p-3 rounded overflow-auto">
                  {JSON.stringify(statsQuery.data?.data ?? {}, null, 2)}
                </pre>
              )}
            </Card>

            <div className="col-span-3" />
          </div>

          {/* Create Admin Modal */}
          <Modal isOpen={isCreateOpen} onClose={() => setCreateOpen(false)} title="Create Admin" size="md">
            <AdminUserForm
              mode="create"
              isSubmitting={createMutation.isPending}
              onSubmit={async (payload) => {
                await createMutation.mutateAsync(payload as any);
              }}
            />
          </Modal>

          {/* Edit Admin Modal */}
          <Modal isOpen={!!isEditOpen} onClose={() => setEditOpen(null)} title="Edit Admin" size="md">
            {isEditOpen && (
              <AdminUserForm
                mode="edit"
                initial={isEditOpen}
                isSubmitting={updateMutation.isPending}
                onSubmit={async (payload) => {
                  await updateMutation.mutateAsync({
                    id: isEditOpen._id,
                    data: payload as any,
                  });
                }}
              />
            )}
          </Modal>

          {/* Permissions Modal */}
          <Modal isOpen={!!permissionsTarget} onClose={() => setPermissionsTarget(null)} title="Edit Permissions" size="lg">
            {permissionsTarget && (
              <PermissionsForm
                initial={permissionsInitial}
                isSubmitting={permissionsMutation.isPending}
                onSubmit={async (perms) => {
                  await permissionsMutation.mutateAsync({ id: permissionsTarget._id, permissions: perms });
                }}
              />
            )}
          </Modal>
        </div>
      ) : (
        <ApiKeysPanel />
      )}
    </div>
  );
};